import { Box, TextField, Tooltip, Typography } from '@mui/material'
import { RJSFValidationError } from '@rjsf/utils'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { useAppController } from 'customHooks/useAppController'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { bomItemActivitiesSelectors } from 'features/BillOfMaterials/store/selectors/activities/bomItemActivitiesSelectors'
import { WorkingStepResourceSelector } from 'features/BillOfMaterials/WorkingSteps/WorkingStepResourceSelector'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  AvailableWorkingStepDto,
  BoMItemActivityDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { useShowException } from 'utils/useShowException'
import {
  getWorkingStepKey,
  WorkingStepWithData,
} from './types/WorkingStepWithData'
import { NewWorkingStepFormRef, WorkingStepForm } from './WorkingStepForm'
import { WorkingStepSelectorPanel } from './WorkingStepSelectorPanel'

const EMPTY_ARRAY = []

type Props = {
  /**
   * the bom items to which the changes will be applied
   */
  affectedBomItemPointers: Array<BomItemPointer>
  activeWorkingStep: BoMItemActivityDto
  /**
   * use this if you don't want state beeing set in the url
   * used in RoutingHeader to avoid modifying the url since we are dealing with multiple bom items
   * @param workingStepType
   * @returns
   */
  setActiveWorkingStep?: (workingStepType: WorkingStepType) => void
  markDirty: (onSave: () => void | Promise<void>, actionName: string) => void
  markClean: (actionName: string) => void
  /**
   * disables the resource selectors inside the working step form
   * when used in the RoutingHeader, modifiying resources will modify also the headers,
   * therefore we disable it to avoid confusion
   */
  disableResourceSelectors?: boolean
}

const makeEmptyDrafts = () => {
  return {
    addedOrUpdated: [],
    removed: [],
  }
}

const useStyles = makeStyles({ name: 'newWorkingSteps' })((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    '& .ws__selector': {
      borderTop: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      '& .MuiAutocomplete-inputRoot': {
        display: 'flex',
        gap: theme.spacing(3),
      },
    },
    '& .ws__active': {
      backgroundColor: theme.palette.action.selected,
      textDecorationColor: theme.palette.primary.main,
    },
    '& .ws__list__root': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    '& .ws__search': {
      padding: theme.spacing(1),
    },
    '& .ws__list': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minWidth: '320px',
    },
    '& .ws__list__container': {
      height: '100%',
      width: '320px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'scroll',
      overflowX: 'hidden',
    },
    '& .ws__list__title': {
      padding: theme.spacing(1, 2),
    },
    '& .ws__item': {
      padding: theme.spacing(0.5, 2),
      cursor: 'pointer',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '& .ws__item__description': {
      display: 'grid',
      gridTemplateColumns: '32px 1fr',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    '& .ws__item__actions': {
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: theme.spacing(5),
      alignItems: 'center',
      '& span': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      },
      '& button': {
        marginLeft: 'auto',
      },
    },
    '& .ws__active_ws': {
      borderTop: `2px solid ${theme.palette.divider}`,
      overflow: 'auto',
      backgroundColor: theme.palette.background.paper,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    '& .ws__active__data': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      '& .ws__active__data__header': {
        padding: theme.spacing(1, 2),
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
      },
      '& .ws__active__data__resource': {
        padding: theme.spacing(0, 2),
        display: 'flex',
        gap: theme.spacing(2),
        flexDirection: 'column',
      },
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .ws__save__actions': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      marginTop: 'auto',
      position: 'sticky',
      bottom: 0,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1),
      borderTop: `2px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .ws__form': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingBottom: theme.spacing(4),
      height: '100%',
      width: '100%',
    },
    '& .ws__form__validations': {
      // width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      justifySelf: 'flex-start',
      paddingLeft: theme.spacing(2),
      gap: theme.spacing(1),
    },
    // '& .ws__form__input': {
    //   '& input': {
    //     fontSize: theme.typography.caption.fontSize,
    //     padding: theme.spacing(0.2, 0, 0),
    //     '&[type="number"]': {
    //       textAlign: 'right',
    //     },
    //   },
    // },
    '& .ws__form__wrapper': {
      width: '100%',
      height: '100%',
    },
    '& .ws__active__editing__list': {
      '& li': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1),
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  },
}))

export const WorkingStepsContent = (props: Props) => {
  const { classes } = useStyles()
  const { t } = useTranslation()
  const { affectedBomItemPointers, markClean, markDirty } = props

  const workingStepFormRef = useRef<NewWorkingStepFormRef>(null)

  const openDetails = useBomItemDetailsDialog()

  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()

  const { projectId } = useOrganizationAndProjectContext()
  const dispatch = useAppDispatch()

  const { controller, loading } = useAppController(() => {
    return new WorkingStepsController()
  })
  const ShowException = useShowException(t('project:working-steps'))

  const drafts = useRef<{
    addedOrUpdated: WorkingStepWithData[]
    removed: Array<{
      activityId: string
      bomItemActivityIds?: Record<string, string>
      primaryWorkingStep?: WorkingStepType
      secondaryWorkingStep?: WorkingStepType
    }>
  }>(makeEmptyDrafts())

  const isMounted = useRef(true)
  const isSavingRef = useRef(false)
  const isRemovingRef = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => {
      drafts.current = makeEmptyDrafts()
      isMounted.current = false
    }
  }, [])

  const [availableWorkingSteps, setAvailableWorkingSteps] =
    useState<AvailableWorkingStepDto[]>(EMPTY_ARRAY)

  const [editableWorkingSteps, setEditableWorkingSteps] =
    useState<WorkingStepWithData[]>(EMPTY_ARRAY)

  const originalWorkingStepsFromStore = useAppSelector(
    bomItemActivitiesSelectors.allActivities(affectedBomItemPointers[0]),
    isEqual,
  )

  const affectedActivities = useAppSelector(
    (state) =>
      affectedBomItemPointers.map((pointer) =>
        bomItemActivitiesSelectors.allActivities(pointer)(state),
      ),
    isEqual,
  )

  const [activeWorkingStep, setActiveWorkingStep] =
    useState<WorkingStepWithData | null>(props.activeWorkingStep)

  const { setActiveWorkingStep: setActiveWorkingStepProp } = props

  const withActivityComments = useCallback(
    (workingSteps: WorkingStepWithData[] | undefined) => {
      const removedActivityIds = new Set(
        drafts.current.removed.map((workingStep) => workingStep.activityId),
      )

      return workingSteps
        ?.filter(
          (workingStep) =>
            !removedActivityIds.has(workingStep.bomItemActivityId),
        )
        .map((workingStep) => {
          const activity = originalWorkingStepsFromStore?.find(
            (x) => x.id === workingStep.bomItemActivityId,
          )
          const matchingActivityIndex = originalWorkingStepsFromStore
            ?.filter(
              (candidate) =>
                candidate.secondaryWorkingStep ===
                workingStep.secondaryWorkingStep,
            )
            .findIndex(
              (candidate) => candidate.id === workingStep.bomItemActivityId,
            )
          const bomItemActivityIds =
            matchingActivityIndex >= 0
              ? affectedBomItemPointers.reduce<Record<string, string>>(
                  (activityIds, pointer, pointerIndex) => {
                    const matchingActivity = affectedActivities[pointerIndex]
                      ?.filter(
                        (candidate) =>
                          candidate.secondaryWorkingStep ===
                          workingStep.secondaryWorkingStep,
                      )
                      [matchingActivityIndex]

                    if (matchingActivity?.id) {
                      activityIds[pointer.id] = matchingActivity.id
                    }

                    return activityIds
                  },
                  {},
                )
              : undefined

          return {
            ...workingStep,
            bomItemActivityIds,
            comment: workingStep.comment ?? activity?.comment,
          }
        })
    },
    [
      affectedActivities,
      affectedBomItemPointers,
      originalWorkingStepsFromStore,
    ],
  )

  const handleOpenDetails = useCallback(
    (
      bomItemPointer: BomItemPointer,
      bomItemPointers: Array<BomItemPointer>,
      workingStepType: WorkingStepType,
      /**
       * the specific bom item activity id to open, required to correctly
       * disambiguate between multiple activities that share the same
       * working step type (e.g. two "Cleaning" activities)
       */
      activityId?: string,
    ) => {
      if (setActiveWorkingStepProp) {
        setActiveWorkingStepProp(workingStepType)
      } else {
        openDetails('workingsteps', {
          bomItemPointer: bomItemPointer,
          affectedBomItemPointers: bomItemPointers,
          workingStep: workingStepType,
          workingStepActivityId: activityId,
        })
      }
    },
    [openDetails, setActiveWorkingStepProp],
  )

  /**
   * Matches a working step in local state against the (possibly stale)
   * active working step coming from props. Prefers matching by activity id,
   * since matching only by working step type is ambiguous when a bom item
   * has multiple activities of the same working step type
   * (e.g. two "Cleaning" activities).
   */
  const matchesActiveWorkingStep = useCallback(
    (candidate: WorkingStepWithData | null | undefined) => {
      if (!candidate || !props.activeWorkingStep) {
        return false
      }

      if (candidate.bomItemActivityId && props.activeWorkingStep.id) {
        return candidate.bomItemActivityId === props.activeWorkingStep.id
      }

      return (
        candidate.secondaryWorkingStep ===
        props.activeWorkingStep?.secondaryWorkingStep
      )
    },
    [props.activeWorkingStep],
  )

  useEffect(() => {
    // Skip when the remove handler already selected the correct step;
    // props.activeWorkingStep is still stale at this point and would
    // cause the effect to fall back to editableWorkingSteps[0].
    if (isRemovingRef.current) {
      isRemovingRef.current = false
      return
    }

    if (props.activeWorkingStep) {
      const alreadyEditedWs = drafts.current.addedOrUpdated.find((x) =>
        matchesActiveWorkingStep(x),
      )
      if (alreadyEditedWs) {
        setActiveWorkingStep(alreadyEditedWs)
        return
      }

      const existing = editableWorkingSteps.find((x) =>
        matchesActiveWorkingStep(x),
      )

      if (existing) {
        setActiveWorkingStep(existing)
        return
      }

      // fallback to the first editable working step
      if (editableWorkingSteps.length > 0) {
        setActiveWorkingStep(editableWorkingSteps[0])
        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          editableWorkingSteps[0].secondaryWorkingStep,
          editableWorkingSteps[0].bomItemActivityId,
        )
        return
      }

      // if no editable working step, clear activeWorkingStep
      setActiveWorkingStep(null)
      handleOpenDetails(
        props.affectedBomItemPointers[0],
        props.affectedBomItemPointers,
        null,
      )
      return
    }
  }, [
    editableWorkingSteps,
    handleOpenDetails,
    matchesActiveWorkingStep,
    props.activeWorkingStep,
    props.affectedBomItemPointers,
  ])

  const firstAffectedBomitemPointerId = affectedBomItemPointers?.[0]?.id

  const getAvailableWorkingSteps = useCallback(async () => {
    return controller.GetWorkingSteps(projectId, firstAffectedBomitemPointerId)
  }, [controller, firstAffectedBomitemPointerId, projectId])

  const saveWorkingStepChanges = useCallback(async () => {
    try {
      isSavingRef.current = true
      await controller.upInsertAndRemoveWorkingStepsWithData(
        affectedBomItemPointers,
        drafts.current.addedOrUpdated,
        drafts.current.removed,
      )

      setValidations((prev) => {
        const newValidations = { ...prev }

        if (activeWorkingStep) {
          delete newValidations[getWorkingStepKey(activeWorkingStep)]
        }

        return newValidations
      })

      drafts.current = makeEmptyDrafts()
      markClean('handleSaveWorkingStepContent')

      if (isMounted.current) {
        const refreshed = await getAvailableWorkingSteps()

        startTransition(async () => {
          // setAvailableWorkingSteps(() => {
          //   if (refreshed.availableWorkingSteps) {
          //     return refreshed.availableWorkingSteps
          //   }

          //   return []
          // })

          setEditableWorkingSteps(
            withActivityComments(refreshed?.selectedWorkingSteps) ?? [],
          )

          if (activeWorkingStep) {
            await workingStepFormRef.current?.refreshData()
          }
        })

        isSavingRef.current = false
        return refreshed
      }

      isSavingRef.current = false
      return undefined
    } catch (err) {
      isSavingRef.current = false
      ShowException(err)
      throw err
    }
  }, [
    controller,
    affectedBomItemPointers,
    markClean,
    getAvailableWorkingSteps,
    activeWorkingStep,
    ShowException,
    withActivityComments,
  ])

  const handleSave = useCallback(async () => {
    await saveWorkingStepChanges()
  }, [saveWorkingStepChanges])

  const [validations, setValidations] = useState<
    Partial<Record<string, RJSFValidationError[]>>
  >({})

  const handleRemoveWorkingStep = useCallback(
    (wsToRemove: WorkingStepWithData) => {
      //remove from drafts
      if (drafts.current.addedOrUpdated.length > 0) {
        drafts.current.addedOrUpdated = drafts.current.addedOrUpdated.filter(
          (x) => x !== wsToRemove,
        )
      }

      // if it has an activityId, it exists on the server and needs to be deleted
      if (wsToRemove.bomItemActivityId) {
        drafts.current.removed.push({
          activityId: wsToRemove.bomItemActivityId,
          bomItemActivityIds: wsToRemove.bomItemActivityIds,
          primaryWorkingStep: wsToRemove.primaryWorkingStep,
          secondaryWorkingStep: wsToRemove.secondaryWorkingStep,
        })
        markDirty(handleSave, 'handleSaveWorkingStepContent')
      }

      // remove it from the editable list
      setEditableWorkingSteps((prev) => {
        const removedIndex = prev.indexOf(wsToRemove)
        const remaining = prev.filter((x) => x !== wsToRemove)

        // if the removed step was the active one, select the previous step
        // in the list, or clear the selection entirely
        const isActiveBeingRemoved =
          activeWorkingStep === wsToRemove ||
          (wsToRemove.bomItemActivityId &&
            wsToRemove.bomItemActivityId ===
              activeWorkingStep?.bomItemActivityId)

        if (isActiveBeingRemoved) {
          // Signal the sync effect to skip its next run — it would
          // otherwise override our selection with editableWorkingSteps[0]
          // because props.activeWorkingStep is still stale.
          isRemovingRef.current = true

          if (remaining.length > 0) {
            const nextIndex = Math.min(
              Math.max(removedIndex - 1, 0),
              remaining.length - 1,
            )
            const next = remaining[nextIndex]
            setActiveWorkingStep(next)
            handleOpenDetails(
              props.affectedBomItemPointers[0],
              props.affectedBomItemPointers,
              next.secondaryWorkingStep,
              next.bomItemActivityId,
            )
          } else {
            setActiveWorkingStep(null)
            handleOpenDetails(
              props.affectedBomItemPointers[0],
              props.affectedBomItemPointers,
              null,
            )
          }
        }

        return remaining
      })
    },
    [
      activeWorkingStep,
      handleOpenDetails,
      handleSave,
      markDirty,
      props.affectedBomItemPointers,
    ],
  )

  const handleAddWorkingStep = useCallback(
    async (workingStepType: WorkingStepType) => {
      const previousActiveWorkingStep = activeWorkingStep
      const hadPendingChangesBeforeAdd = Boolean(
        drafts.current.addedOrUpdated.length || drafts.current.removed.length,
      )
      // check if the type was previously removed in this session (restore case)
      const removedWs = drafts.current.removed.find(
        (x) => x?.secondaryWorkingStep === workingStepType,
      )
      const existingActivityIds = new Set(
        editableWorkingSteps
          .map((workingStep) => workingStep.bomItemActivityId)
          .filter(Boolean),
      )

      if (removedWs) {
        // restore a previously removed working step
        drafts.current.removed = drafts.current.removed.filter(
          (x) => x !== removedWs,
        )
        const restoredWs: WorkingStepWithData = {
          bomItemActivityId: removedWs.activityId,
          primaryWorkingStep: removedWs.primaryWorkingStep,
          secondaryWorkingStep: removedWs.secondaryWorkingStep,
        }
        setEditableWorkingSteps((prev) => [...prev, restoredWs])

        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          restoredWs?.secondaryWorkingStep,
          restoredWs?.bomItemActivityId,
        )

        markDirty(handleSave, 'handleSaveWorkingStepContent')
      } else {
        // add a new working step (even if the same type already exists)
        const newWs: WorkingStepWithData = {
          primaryWorkingStep: workingStepType,
          secondaryWorkingStep: workingStepType,
          formData: null,
        }

        drafts.current.addedOrUpdated.push(newWs)
        setEditableWorkingSteps((prev) => [...prev, newWs])
        setActiveWorkingStep(newWs)

        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          newWs?.secondaryWorkingStep,
          newWs?.bomItemActivityId,
        )

        markDirty(handleSave, 'handleSaveWorkingStepContent')

        let refreshed: Awaited<ReturnType<typeof saveWorkingStepChanges>>

        try {
          refreshed = await saveWorkingStepChanges()
        } catch (error) {
          drafts.current.addedOrUpdated = drafts.current.addedOrUpdated.filter(
            (x) => x !== newWs,
          )
          setEditableWorkingSteps((prev) => prev.filter((x) => x !== newWs))
          setActiveWorkingStep(previousActiveWorkingStep)

          if (previousActiveWorkingStep) {
            handleOpenDetails(
              props.affectedBomItemPointers[0],
              props.affectedBomItemPointers,
              previousActiveWorkingStep.secondaryWorkingStep,
              previousActiveWorkingStep.bomItemActivityId,
            )
          } else {
            handleOpenDetails(
              props.affectedBomItemPointers[0],
              props.affectedBomItemPointers,
              null,
            )
          }

          if (!hadPendingChangesBeforeAdd) {
            markClean('handleSaveWorkingStepContent')
          }

          return
        }

        const refreshedWorkingSteps =
          withActivityComments(refreshed?.selectedWorkingSteps) ?? []
        const savedWorkingStep =
          refreshedWorkingSteps.find(
            (workingStep) =>
              workingStep.secondaryWorkingStep === workingStepType &&
              !existingActivityIds.has(workingStep.bomItemActivityId),
          ) ??
          refreshedWorkingSteps.findLast(
            (workingStep) =>
              workingStep.secondaryWorkingStep === workingStepType,
          )

        if (savedWorkingStep) {
          setActiveWorkingStep(savedWorkingStep)
          handleOpenDetails(
            props.affectedBomItemPointers[0],
            props.affectedBomItemPointers,
            savedWorkingStep.secondaryWorkingStep,
            savedWorkingStep.bomItemActivityId,
          )
        }
      }
    },
    [
      activeWorkingStep,
      editableWorkingSteps,
      handleOpenDetails,
      handleSave,
      markClean,
      markDirty,
      props.affectedBomItemPointers,
      saveWorkingStepChanges,
      withActivityComments,
    ],
  )

  const handleReorderWorkingSteps = useCallback(
    (movedActivityId: string, moveAfterActivityId?: string) => {
      setEditableWorkingSteps((prev) => {
        const workingStepToMove = prev.find(
          (ws) => ws.bomItemActivityId === movedActivityId,
        )

        if (!workingStepToMove) {
          return prev
        }

        const reorderedWorkingSteps = prev.filter(
          (ws) => ws.bomItemActivityId !== movedActivityId,
        )

        if (!moveAfterActivityId) {
          return [workingStepToMove, ...reorderedWorkingSteps]
        }

        const moveAfterIndex = reorderedWorkingSteps.findIndex(
          (ws) => ws.bomItemActivityId === moveAfterActivityId,
        )

        if (moveAfterIndex === -1) {
          return prev
        }

        reorderedWorkingSteps.splice(moveAfterIndex + 1, 0, workingStepToMove)
        return reorderedWorkingSteps
      })
    },
    [],
  )

  const handleChangeActiveWorkingStep = useCallback(
    <T extends keyof WorkingStepWithData>(
      field: T,
      value: WorkingStepWithData[T],
    ) => {
      if (!activeWorkingStep) {
        return
      }

      const updated = {
        ...activeWorkingStep,
        [field]: value,
      }

      // Match by activity id when available, since matching only by working
      // step type is ambiguous when a bom item has multiple activities of
      // the same working step type. For not-yet-saved working steps
      // (no activity id yet), fall back to matching the exact draft object
      // that is currently being edited.
      const existingIndex = drafts.current.addedOrUpdated.findIndex((x) =>
        updated.bomItemActivityId
          ? x?.bomItemActivityId === updated.bomItemActivityId
          : x === activeWorkingStep,
      )
      if (existingIndex >= 0) {
        drafts.current.addedOrUpdated[existingIndex] = updated
      } else {
        drafts.current.addedOrUpdated.push(updated)
      }

      setValidations((prev) => {
        const newValidations = { ...prev }
        if (newValidations) {
          delete newValidations[getWorkingStepKey(updated)]
        }

        return newValidations
      })

      workingStepFormRef.current?.getValidations()

      setActiveWorkingStep(updated)
      markDirty(handleSave, 'handleSaveWorkingStepContent')
    },
    [activeWorkingStep, handleSave, markDirty],
  )

  const projectVersion = useAppSelector((s) => s.project.activeProject?.version)
  const lastProjectVersion = useRef(projectVersion)

  useEffect(() => {
    // Skip refresh while a save is in-flight to avoid flashing stale data
    if (isSavingRef.current) {
      return
    }
    startTransition(() => {
      getAvailableWorkingSteps().then((data) => {
        if (!data) {
          return
        }

        setAvailableWorkingSteps((current) => {
          if (current !== EMPTY_ARRAY) {
            return current
          }

          return (
            data.availableWorkingSteps?.filter(
              (x) =>
                x.secondaryWorkingStep &&
                x.secondaryWorkingStep !== WorkingStepType.NotInitialized,
            ) ?? EMPTY_ARRAY
          )
        })

        setEditableWorkingSteps(
          withActivityComments(data.selectedWorkingSteps) ?? [],
        )

        lastProjectVersion.current = projectVersion
      })
    })
  }, [getAvailableWorkingSteps, projectVersion, withActivityComments])

  const editableWorkingStepsType = useMemo(() => {
    return editableWorkingSteps.map((x) => x?.secondaryWorkingStep)
  }, [editableWorkingSteps])

  const handleResetChanges = useCallback(() => {
    drafts.current = makeEmptyDrafts()
    markClean('handleSaveWorkingStepContent')

    if (activeWorkingStep) {
      setActiveWorkingStep(null)
    }

    if (validations) {
      setValidations({})
    }

    handleOpenDetails(
      props.affectedBomItemPointers[0],
      props.affectedBomItemPointers,
      null,
    )

    // Re-fetch working steps to restore editableWorkingSteps to server state
    getAvailableWorkingSteps().then((data) => {
      if (data?.selectedWorkingSteps) {
        setEditableWorkingSteps(
          withActivityComments(data.selectedWorkingSteps) ?? [],
        )
      }
    })

    dispatch(fetchProject({ projectId }))
  }, [
    activeWorkingStep,
    dispatch,
    getAvailableWorkingSteps,
    handleOpenDetails,
    markClean,
    projectId,
    props.affectedBomItemPointers,
    validations,
    withActivityComments,
  ])

  const handleUndoRemoveWorkingStep = useCallback(
    (activityId: string) => {
      // find the working step in the removed drafts
      const removedWs = drafts.current.removed.find(
        (x) => x?.activityId === activityId,
      )
      if (!removedWs) {
        return
      }

      // remove from the removed drafts
      drafts.current.removed = drafts.current.removed.filter(
        (x) => x?.activityId !== activityId,
      )
      // add back to the editable working steps
      setEditableWorkingSteps((prev) => [
        ...prev,
        {
          bomItemActivityId: removedWs.activityId,
          primaryWorkingStep: removedWs.primaryWorkingStep,
          secondaryWorkingStep: removedWs.secondaryWorkingStep,
        },
      ])
      markDirty(handleSave, 'handleSaveWorkingStepCcontent')
    },
    [handleSave, markDirty],
  )

  return (
    <div className={classes.root}>
      <div className={'ws__selector'}>
        <WorkingStepSelectorPanel
          onWorkingStepRemoved={handleRemoveWorkingStep}
          bomItemPointer={props.affectedBomItemPointers[0]}
          currentWorkingSteps={editableWorkingSteps}
          availableWorkingSteps={availableWorkingSteps}
          activeWorkingStepId={activeWorkingStep?.bomItemActivityId || null}
          onActiveWorkingStepChange={(ws) => {
            setActiveWorkingStep(ws)
            handleOpenDetails(
              props.affectedBomItemPointers[0],
              props.affectedBomItemPointers,
              ws.secondaryWorkingStep,
              ws.bomItemActivityId,
            )
          }}
          onWorkingStepAdded={handleAddWorkingStep}
          onWorkingStepsReordered={handleReorderWorkingSteps}
          markDirty={markDirty}
          handleSave={handleSave}
          validations={validations}
          disableResourceSelectors={props.disableResourceSelectors}
          affectedBomItemPointers={props.affectedBomItemPointers}
        />
      </div>
      <div className={'ws__active_ws'}>
        {!activeWorkingStep && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {t(
                'project:select-or-add-working-step-to-edit',
                'select or add a working step to edit',
              )}
            </Typography>
          </div>
        )}
        {activeWorkingStep && (
          <div className={'ws__active__data'}>
            <div className={'ws__active__data__header'}>
              <WorkingStepIconSvg
                workingStepType={activeWorkingStep?.secondaryWorkingStep}
                attributes={{ width: '42px', height: '42px' }}
              />
              <Typography sx={{ fontWeight: 600 }}>
                {getLocalizedWorkingStep(
                  activeWorkingStep?.secondaryWorkingStep,
                )}
              </Typography>
            </div>
            <div className={'ws__active__data__resource'}>
              <div>
                <WorkingStepResourceSelector
                  bomItemPointersAndActivities={affectedBomItemPointers.map(
                    (bomItemPointer) => ({
                      bomItemPointer,
                      activityId:
                        activeWorkingStep?.bomItemActivityIds?.[
                          bomItemPointer.id
                        ],
                    }),
                  )}
                  activityWorkingStepType={
                    activeWorkingStep?.secondaryWorkingStep
                  }
                  bomItemActivityId={activeWorkingStep?.bomItemActivityId}
                  useStoreActivity
                  workingStep={activeWorkingStep}
                  fullWidth
                  withLabel
                  disabled={props.disableResourceSelectors}
                />
              </div>
              <TextField
                fullWidth
                placeholder="add any notes for this working step"
                label={t('project:notes', 'notes')}
                data-testid="bom-details-working-step-notes"
                value={
                  activeWorkingStep?.comment ??
                  props.activeWorkingStep?.comment ??
                  ''
                }
                onChange={(e) => {
                  handleChangeActiveWorkingStep(
                    'comment',
                    e.currentTarget.value,
                  )
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const text = e.dataTransfer.getData('text/plain')
                  if (text) {
                    handleChangeActiveWorkingStep('comment', text)
                  }
                }}
              />
            </div>
            {/*
            <JsonViewer data={{ props: props ?? 'no form data' }} />
 */}
            {/* <JsonViewer data={validations} /> */}
            <div className={'ws__form'}>
              {activeWorkingStep && (
                <>
                  {/* {validations[getWorkingStepKey(activeWorkingStep)] && (
                    <>
                      <span className={'ws__form__validations'}>
                        <WarningAmberOutlined color="warning" />
                        <Typography color="error" variant="body2">
                          {t(
                            'project:working-step-form-has-validation-issues',
                            {
                              defaultValue: '{{count}} issues',
                              count:
                                uniqBy(
                                  validations[
                                    getWorkingStepKey(activeWorkingStep)
                                  ],
                                  (x) =>
                                    x.property.substring(
                                      0,
                                      x.property.lastIndexOf('.'),
                                    ),
                                )?.length || 0,
                            },
                          )}
                        </Typography>
                        <JsonViewer
                          data={
                            validations[getWorkingStepKey(activeWorkingStep)]
                          }
                        />
                      </span>
                    </>
                  )} */}
                  <WorkingStepForm
                    ref={workingStepFormRef}
                    bomItemPointer={props.affectedBomItemPointers[0]}
                    workingStep={activeWorkingStep}
                    onChange={handleChangeActiveWorkingStep}
                    onError={(errors: RJSFValidationError[]) => {
                      errors = errors.filter(
                        (x) =>
                          x.property !== '.' &&
                          x.params?.missingProperty !== 'quantityType' &&
                          x.params?.missingProperty !== 'unit',
                      )

                      setValidations((prev) => {
                        return {
                          ...prev,
                          [getWorkingStepKey(activeWorkingStep)]: errors,
                        }
                      })
                    }}
                    projectId={projectId}
                    controller={controller}
                    loading={loading}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <div className={'ws__save__actions'}>
          {Object.values(drafts.current).length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                  // borderTop: (t) => `2px solid ${t.palette.divider}`,
                  // flex: '0 0 52px',
                }}
              >
                <Tooltip
                  placement="top"
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    tooltip: {
                      sx: { p: 0 },
                    },
                  }}
                  title={
                    drafts.current.addedOrUpdated.length === 0 ? (
                      '' // to avoid showing empty tooltip
                    ) : (
                      <ul className={'ws__active__editing__list'}>
                        {drafts.current.addedOrUpdated?.map((x) => (
                          <li
                            key={getWorkingStepKey(x)}
                            onClick={() => {
                              setActiveWorkingStep(x)
                            }}
                          >
                            <WorkingStepIconSvg
                              workingStepType={x?.secondaryWorkingStep}
                              attributes={{ width: '32px', height: '32px' }}
                            />
                            <Typography variant="body2">
                              {getLocalizedWorkingStep(x?.secondaryWorkingStep)}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )
                  }
                >
                  <span>
                    <ValueLabel
                      label={t('project:additional-working-steps--to-add')}
                      value={drafts.current?.addedOrUpdated?.length || 0}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  placement="top"
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    tooltip: {
                      sx: { p: 0 },
                    },
                  }}
                  title={
                    drafts.current.removed.length === 0 ? (
                      '' // to avoid showing empty tooltip
                    ) : (
                      <ul className={'ws__active__editing__list'}>
                        {drafts.current.removed?.map((x) => (
                          <li
                            key={x?.activityId}
                            onClick={() => {
                              handleUndoRemoveWorkingStep(x?.activityId)
                            }}
                          >
                            <WorkingStepIconSvg
                              workingStepType={x?.secondaryWorkingStep}
                              attributes={{ width: '32px', height: '32px' }}
                            />
                            <Typography variant="body2">
                              {getLocalizedWorkingStep(x?.secondaryWorkingStep)}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )
                  }
                >
                  <span>
                    <ValueLabel
                      label={t('project:additional-working-steps--to-remove')}
                      value={drafts.current?.removed?.length || 0}
                    />
                  </span>
                </Tooltip>
              </Box>
              <DialogCloseSaveButtons
                onCloseButtonClicked={handleResetChanges}
                onSaveButtonClicked={handleSave}
                buttonsDisabled={
                  drafts.current.addedOrUpdated.length === 0 &&
                  drafts.current.removed.length === 0
                }
                cancelButtonTranslationKey="common:cancel"
              />
            </>
          )}
        </div>
      </div>
      {/* <div>
        <JsonViewer data={drafts.current} />
        <JsonViewer data={validations} />
        <Button onClick={() => workingStepFormRef.current?.getValidations()}>
          Get Validations
        </Button>
      </div> */}
    </div>
  )
}
